import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { readerApi, familyApi, reportApi, rawChapterApi, chapterApi, challengeApi, gamificationApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ReaderSelector from '../components/reporting/ReaderSelector';
import BibleProgressCalendar from '../components/progress/BibleProgressCalendar';
import ProgressSummary from '../components/progress/ProgressSummary';
import MoreStats from '../components/progress/MoreStats';
import ProgressContextRow from '../components/progress/ProgressContextRow';
import ManageReportsPage from './ManageReportsPage';
import YearScopeToggle from '../components/progress/YearScopeToggle';
import { hasSimplifiedSetup } from '../utils/userSetup';
import {
  countFinishedBooks,
  countChaptersInWindow
} from '../utils/progressStats';
import './ProgressPage.css';

const ProgressPage = () => {
  const { preferences } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const competitiveMode = Boolean(preferences?.competitiveMode);
  const hashSection = (location.hash || '#overview').replace('#', '') || 'overview';
  // #points is an old bookmark from before points were retired.
  const section = hashSection === 'points' ? 'overview' : hashSection;

  const [readers, setReaders] = useState([]);
  const [families, setFamilies] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [reports, setReports] = useState([]);
  const [bulkChapters, setBulkChapters] = useState(0);
  const [readerProfile, setReaderProfile] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedReaders, setSelectedReaders] = useState([]);
  const [readerMode, setReaderMode] = useState('primary');
  const [selectedFamily, setSelectedFamily] = useState(null);
  // Progress starts at this year's reading; 'all' brings back earlier years.
  const [yearScope, setYearScope] = useState('season');
  const [seasonStart, setSeasonStart] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedReaders.length > 0) {
      loadProgressData();
      loadReaderProfile(selectedReaders[0].id);
    }
  }, [selectedReaders, yearScope]);

  useEffect(() => {
    if (section === 'books') {
      document.getElementById('progress-books')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [section, loading]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [readersRes, familiesRes, chaptersRes, challengeRes] = await Promise.all([
        readerApi.getReaders(),
        familyApi.getFamilies(),
        chapterApi.getChapters({ limit: 1200 }),
        challengeApi.getActive().catch(() => ({ data: {} }))
      ]);

      setReaders(readersRes.data.readers || []);
      setFamilies(familiesRes.data.families || []);
      setChapters(chaptersRes.data.chapters || []);
      setChallenge(challengeRes.data?.challenge || null);

      const primaryReader = readersRes.data.readers?.find(r => r.isPrimary);
      if (primaryReader) {
        setSelectedReaders([primaryReader]);
      }

      setError(null);
    } catch (err) {
      console.error('Failed to load initial data:', err);
      setError('Failed to load progress. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadProgressData = async () => {
    try {
      const readerIds = selectedReaders.map(r => r.id);
      const scope = yearScope === 'season' ? { season: 'current' } : {};
      const [reportsResponses, bulkResponses] = await Promise.all([
        Promise.all(readerIds.map(readerId => reportApi.getReports({ readerId, limit: 5000, ...scope }))),
        // Bulk logs carry a chapter count but no chapterId, so they never come
        // back from /reports. Fetched separately so the totals on this page add
        // up to the figure the stats card shows for the same years.
        Promise.all(readerIds.map(readerId => rawChapterApi.getReports({ readerId, limit: 1, ...scope })))
      ]);

      setReports(reportsResponses.flatMap(response => response.data.reports || []));
      const start = reportsResponses.find(response => response.data.seasonStart)?.data.seasonStart;
      if (start) setSeasonStart(start);
      setBulkChapters(
        bulkResponses.reduce((sum, response) => sum + (response.data.totalChapters || 0), 0)
      );
    } catch (err) {
      console.error('Failed to load progress data:', err);
      setError('Failed to load reading progress. Please try again.');
    }
  };

  const loadReaderProfile = async (readerId) => {
    try {
      const response = await gamificationApi.getReaderProfile(readerId);
      setReaderProfile(response.data.profile);
    } catch (err) {
      console.error('Failed to load reader profile:', err);
      setReaderProfile(null);
    }
  };

  const handleReaderModeChange = (mode) => {
    setReaderMode(mode);
    setSelectedReaders([]);
    setSelectedFamily(null);

    if (mode === 'primary') {
      const primaryReader = readers.find(r => r.isPrimary);
      if (primaryReader) {
        setSelectedReaders([primaryReader]);
      }
    }
  };

  const handleFamilySelect = (family) => {
    setSelectedFamily(family);
    setSelectedReaders(family?.readers || []);
  };

  const handleReaderToggle = (reader) => {
    setSelectedReaders(prev => {
      const isSelected = prev.some(r => r.id === reader.id);
      if (isSelected) {
        return prev.filter(r => r.id !== reader.id);
      }
      return [...prev, reader];
    });
  };

  const getProgressStats = () => {
    if (!chapters.length) {
      return {
        totalChapters: 0,
        readChapters: 0,
        uniqueChaptersRead: 0,
        percentComplete: 0,
        oldTestamentRead: 0,
        newTestamentRead: 0,
        totalReports: 0,
        bulkChapters: 0,
        totalChaptersRead: 0
      };
    }

    const uniqueChaptersRead = new Set(reports.map(r => r.chapterId)).size;
    const oldTestamentChapters = new Set(
      reports.filter(r => r.chapter?.testament === 'Old').map(r => r.chapterId)
    ).size;
    const newTestamentChapters = new Set(
      reports.filter(r => r.chapter?.testament === 'New').map(r => r.chapterId)
    ).size;

    return {
      totalChapters: chapters.length,
      readChapters: reports.length,
      uniqueChaptersRead,
      percentComplete: Math.round((uniqueChaptersRead / chapters.length) * 100),
      oldTestamentRead: oldTestamentChapters,
      newTestamentRead: newTestamentChapters,
      totalReports: reports.length,
      bulkChapters,
      totalChaptersRead: reports.length + bulkChapters
    };
  };

  const getChapterReadCounts = () => {
    const readCounts = {};
    reports.forEach(report => {
      readCounts[report.chapterId] = (readCounts[report.chapterId] || 0) + 1;
    });
    return readCounts;
  };

  const showOverview = section !== 'reports';
  const goToSection = (next) => {
    navigate(next === 'overview' ? '/progress' : `/progress#${next}`);
  };

  const renderOverview = () => {
    if (loading) {
      return (
        <div className="progress-page-pane loading">
          <LoadingSpinner size="large" message="Loading progress..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-message">
          <h2>Unable to load progress</h2>
          <p>{error}</p>
          <button onClick={loadInitialData} className="retry-button">
            Try Again
          </button>
        </div>
      );
    }

    const progressStats = getProgressStats();
    const chapterReadCounts = getChapterReadCounts();
    const isSimplifiedSetup = hasSimplifiedSetup(readers, families);
    const books = countFinishedBooks(chapters, chapterReadCounts);
    const derived = {
      booksFinished: books.finished,
      booksTotal: books.total,
      challengeChapters: countChaptersInWindow(reports, challenge?.startDate, challenge?.endDate),
      chaptersRead: reports.length + bulkChapters,
      bulkChapters,
      yearScope
    };

    const singleReader = selectedReaders.length === 1;
    const readerLabel = selectedReaders.length === 0
      ? 'Choose a reader'
      : selectedReaders.length <= 3
        ? selectedReaders.map(r => r.name).join(', ')
        : `${selectedReaders.length} readers`;

    return (
      <>
        <ProgressContextRow
          label={readerLabel}
          picker={isSimplifiedSetup ? null : (
            <ReaderSelector
              readers={readers}
              families={families}
              readerMode={readerMode}
              selectedReaders={selectedReaders}
              selectedFamily={selectedFamily}
              onReaderModeChange={handleReaderModeChange}
              onFamilySelect={handleFamilySelect}
              onReaderToggle={handleReaderToggle}
            />
          )}
        >
          {selectedReaders.length > 0 && (
            <YearScopeToggle value={yearScope} onChange={setYearScope} />
          )}
        </ProgressContextRow>

        {selectedReaders.length === 0 && (
          <div className="progress-section progress-empty">
            <h2>{readers.length === 0 ? 'No reader yet' : 'Choose a reader'}</h2>
            <p>
              {readers.length === 0
                ? 'Add a reader under Family → Readers to start tracking progress.'
                : 'Pick a reader above to see their reading progress.'}
            </p>
            {readers.length === 0 && (
              <Link to="/family#readers" className="progress-empty-link">
                Go to Family → Readers
              </Link>
            )}
          </div>
        )}

        {selectedReaders.length > 0 && (
          <>
            <div className="progress-section">
              <ProgressSummary
                stats={progressStats}
                derived={derived}
                profile={singleReader ? readerProfile : null}
                singleReader={singleReader}
                yearScope={yearScope}
                seasonStart={seasonStart}
                competitiveMode={competitiveMode}
                publicProfileReaderId={singleReader ? selectedReaders[0].id : null}
              />
            </div>

            <div className="progress-section" id="progress-books">
              <BibleProgressCalendar
                chapters={chapters}
                chapterReadCounts={chapterReadCounts}
              />
            </div>

            <MoreStats
              stats={progressStats}
              derived={derived}
              profile={singleReader ? readerProfile : null}
              singleReader={singleReader}
            />
          </>
        )}
      </>
    );
  };

  return (
    <div className="progress-page">
      <div className="progress-header">
        <h1>My Progress</h1>

        <div className="progress-tabs" role="tablist" aria-label="My Progress">
          <button
            type="button"
            role="tab"
            aria-selected={section === 'overview' || section === 'books'}
            className={`progress-tab ${(section === 'overview' || section === 'books') ? 'progress-tab--active' : ''}`}
            onClick={() => goToSection('overview')}
          >
            Overview
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={section === 'reports'}
            className={`progress-tab ${section === 'reports' ? 'progress-tab--active' : ''}`}
            onClick={() => goToSection('reports')}
          >
            Reports
          </button>
        </div>
      </div>

      {section === 'reports' && <ManageReportsPage />}

      {showOverview && renderOverview()}
    </div>
  );
};

export default ProgressPage;
